import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Moon, Sun, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { LOGO_URL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_IDS = ["home", "about", "services", "industries", "locations", "careers", "contact"];

export const Navbar = ({ dark, setDark }) => {
  const { t, lang, change, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeLang = languages.find((l) => l.code === lang);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.6, 0.2, 1] }}
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong shadow-elevated" : "bg-transparent"
      } ${scrolled ? "" : "[&_button]:text-white"}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-8 md:py-4">
        {/* Logo */}
        <button
          onClick={() => go("home")}
          data-testid="nav-logo"
          className="group flex items-center gap-3 ring-focus rounded-lg"
          aria-label="Genius WorkPA — Startseite"
        >
          <div className={`flex h-12 items-center justify-center rounded-xl px-2 transition-all ${scrolled ? "bg-white shadow-elevated ring-1 ring-black/5" : "bg-white/95 backdrop-blur"}`}>
            <img
              src={LOGO_URL}
              alt="Genius WorkPA GmbH"
              className="h-10 w-auto object-contain"
            />
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_IDS.map((id) => (
            <button
              key={id}
              onClick={() => go(id)}
              data-testid={`nav-link-${id}`}
              className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                scrolled
                  ? "text-slate-700 hover:text-[#1E3A8A] dark:text-slate-200 dark:hover:text-white"
                  : "text-white/85 hover:text-white"
              }`}
            >
              <span className="relative z-10">{t.nav[id]}</span>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r from-[#2563EB]/0 to-[#7C3AED]/0 transition-all hover:from-[#2563EB]/10 hover:to-[#7C3AED]/10" />
            </button>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Dark mode toggle */}
          <button
            onClick={() => setDark(!dark)}
            data-testid="dark-mode-toggle"
            aria-label="Toggle theme"
            className="hidden md:flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/70 bg-white/60 text-slate-700 backdrop-blur transition-all hover:scale-105 hover:border-[#7C3AED]/40 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {/* Language switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-switcher"
                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium backdrop-blur transition-all hover:border-[#7C3AED]/40 ${
                  scrolled
                    ? "border-slate-200/70 bg-white/60 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                    : "border-white/20 bg-white/10 text-white"
                }`}
              >
                <Globe className="h-4 w-4" />
                <span className="text-base leading-none">{activeLang?.flag}</span>
                <span className="hidden sm:inline uppercase tracking-wider text-xs">{lang}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="language-menu"
              className="min-w-[180px] glass-strong border border-white/40 dark:border-white/10"
            >
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => change(l.code)}
                  data-testid={`language-option-${l.code}`}
                  className="cursor-pointer gap-3 py-2.5"
                >
                  <span className="text-lg leading-none">{l.flag}</span>
                  <span className="flex-1 font-medium">{l.label}</span>
                  {lang === l.code && <Check className="h-4 w-4 text-[#7C3AED]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* CTA */}
          <Button
            onClick={() => go("contact")}
            data-testid="nav-cta-button"
            className={`hidden md:inline-flex h-10 rounded-full px-5 text-sm font-semibold shadow-elevated transition-all hover:scale-[1.02] ${
              scrolled
                ? "bg-[#0F172A] !text-white hover:bg-[#1E3A8A] dark:bg-white dark:!text-[#0F172A] dark:hover:bg-slate-100"
                : "bg-white !text-[#0F172A] hover:bg-slate-100"
            }`}
          >
            {t.nav.cta}
          </Button>

          {/* Mobile burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-full border ${
              scrolled
                ? "border-slate-200/70 bg-white/60 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                : "border-white/20 bg-white/10 text-white"
            }`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="mobile-menu"
            className="lg:hidden glass-strong border-t border-white/20 dark:border-white/5"
          >
            <div className="mx-auto max-w-7xl px-5 py-4">
              <div className="grid gap-1">
                {NAV_IDS.map((id) => (
                  <button
                    key={id}
                    onClick={() => go(id)}
                    data-testid={`mobile-nav-link-${id}`}
                    className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    {t.nav[id]}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <Button
                  onClick={() => go("contact")}
                  data-testid="mobile-nav-cta"
                  className="flex-1 rounded-full bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]"
                >
                  {t.nav.cta}
                </Button>
                <button
                  onClick={() => setDark(!dark)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 dark:border-white/10"
                  aria-label="Toggle theme"
                >
                  {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
